import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { Ayuda } from '../../components/Ayuda';
import { Spinner } from '../../components/Spinner';
import { EstadoEstudio } from './EstadoEstudio';
import { ModalSeia } from './ModalSeia';
import { colaRuta, type Favorito } from './tipos';

export const AYUDA_REFERENCIAS = 'DIAs terminadas (de AMBEC o del portal e-SEIA) que los agentes usan como ejemplares de estructura, profundidad y criterio en TODOS los chats y capítulos. Sus datos nunca se copian a un proyecto en curso: solo entrenan.';

/** Casos de referencia (2026-10-22: viven en Entrenamiento, no en Agente DIA). Carga sus propios
 *  favoritos de tipo "referencia"; "+ Agregar desde e-SEIA" abre el buscador del portal del SEA. */
export function CasosReferencia({ onChat }: { onChat: (f: Favorito) => void }) {
  const [refs, setRefs] = useState<Favorito[]>([]);
  const [cargando, setCargando] = useState(true);
  const [seiaAbierto, setSeiaAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cargar = useCallback(async () => {
    try {
      const d = await apiFetch<{ favoritos: Favorito[] }>('/api/sharepoint/favoritos');
      setRefs(d.favoritos.filter((f) => f.tipo === 'referencia'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los casos de referencia.');
    } finally {
      setCargando(false);
    }
  }, []);
  useEffect(() => { void cargar(); }, [cargar]);
  useEffect(() => {
    if (!refs.some((f) => ['ingestando', 'analizando', 'enriqueciendo'].includes(f.sync_estado ?? ''))) return;
    const t = window.setInterval(() => void cargar(), 4000);
    return () => window.clearInterval(t);
  }, [refs, cargar]);
  const accion = async (path: string, method: 'POST' | 'DELETE') => {
    try { await apiFetch(path, { method }); await cargar(); } catch (e) { setError(e instanceof Error ? e.message : 'La acción falló.'); }
  };
  return (
    <section className="tarjeta p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-medium text-azul-noche">
          📚 Casos de referencia <span className="font-normal text-gris">( {refs.length} )</span>
          <Ayuda texto={AYUDA_REFERENCIAS} />
        </h2>
        <Ayuda texto="Busca un proyecto APROBADO en el portal público del SEA y tráelo como caso de referencia: Andrés estudia su DIA, ICSARA, Adendas y RCA." toque={false}>
          <button type="button" onClick={() => setSeiaAbierto(true)} className="btn-secundario !h-8 shrink-0 whitespace-nowrap px-3 text-xs">+ Agregar desde e-SEIA</button>
        </Ayuda>
      </div>
      {error && <p role="alert" className="alerta-error mt-3">{error}</p>}
      {cargando ? (
        <div className="flex justify-center py-8"><Spinner tamano="md" /></div>
      ) : refs.length === 0 ? (
        <p className="mt-3 text-sm font-light text-gris">Aún no hay casos de referencia — agrégalos desde el e-SEIA o marca una carpeta del SharePoint como referencia desde "+ Agregar DIA".</p>
      ) : (
        <ul className="mt-2 divide-y divide-slate-100">
          {refs.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-x-2 text-[13px] font-medium text-azul-noche">
                  <span>📚 {f.nombre}</span>
                  {f.origen === 'seia' && (
                    <Ayuda texto="Referencia traída del expediente público del SEA (origen e-SEIA). Solo entrena a los agentes: nunca se mezcla con el proyecto en curso.">
                      <span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">e-SEIA</span>
                    </Ayuda>
                  )}
                </span>
                <span className="block truncate text-[10.5px] font-light text-gris" title={f.ruta}>
                  {f.origen === 'seia' ? `e-SEIA · expediente ${f.ruta.replace(/^seia\//, '')}` : colaRuta(f.ruta)}
                </span>
              </span>
              <EstadoEstudio f={f} />
              <span className="flex shrink-0 items-center gap-2.5">
                <button type="button" onClick={() => onChat(f)} disabled={(f.docs_listos ?? 0) === 0} className="text-xs font-medium text-azul-profundo hover:underline disabled:opacity-40" title="Conversar con Andrés sobre este caso (solo para aprender de él)">Conversar</button>
                <button type="button" disabled={f.sync_estado === 'ingestando'} onClick={() => void accion(`/api/sharepoint/favoritos/${f.id}/ingestar`, 'POST')} className="text-xs font-medium text-azul-profundo hover:underline disabled:opacity-50">
                  {f.sync_estado === 'listo' ? 'Sincronizar' : 'Preparar'}
                </button>
                <button type="button" onClick={() => void accion(`/api/sharepoint/favoritos/${f.id}`, 'DELETE')} className="text-xs font-normal text-gris hover:text-red-600">Quitar</button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {seiaAbierto && (
        <ModalSeia
          yaAgregados={new Set(refs.filter((f) => f.origen === 'seia').map((f) => f.ruta.replace(/^seia\//, '')))}
          onAgregado={cargar}
          onCerrar={() => setSeiaAbierto(false)}
        />
      )}
    </section>
  );
}
